import React from "react";
import { JsonDateParser } from "../../../../../../react-core/Utils/JsonDateParser";
import AriaTooltip from "../../../../../Shared/Tooltip/AriaTooltip";
import { Typography } from "../../../../../Shared/Typography/Typography";
import { CustomerSearchAction } from "../../ClientRecord/Action/CustomerSearchAction";
import { RoutingActions } from "../../../../../../react-core/Flux/Variables";
import { HierarchyLevelName } from "../../../../../../Wrap/Constants/Enums/HierarchyLevelName";
import { Urls } from "../../../../Urls";
import { HierarchyIdHelper } from "../../../../../Shared/Common/HierarchyIdHelper";
import classNames from "classnames";
import { RoleType } from "../../../../../../Wrap/Constants/Enums/RoleType"; 
import { clientTypeEnum } from "../../ClientSearch/Resource/Content";

interface IClientDetailsPopUpProps {
    customerId: number;
    clientType: clientTypeEnum;
    trigger?: "hover" | "click";
}

export const ClientDetailsPopup = (props: IClientDetailsPopUpProps) => {
    if (!props.customerId) { return null; }

    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const [visible, setVisibility] = React.useState(false);
    const [componentMounted, setComponentMounted] = React.useState(false);
    const [customerAge, setCustomerAge] = React.useState(null);
    const [customerDOB, setCustomerDOB] = React.useState(null);
    const [taxIdentifier, setTaxIdentifier] = React.useState(null);
    const [emailAddress, setEmailAddress] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [accountIdWithActiveRoles, setAccountIdWithActiveRole] = React.useState(null);

    const getCustomerDetail = (customerId: number) => {

        setIsLoading(true);
        CustomerSearchAction.getCustomerData(customerId, (result) => {
            if (result?.CustomerTaxResidencyDetails != null && result?.CustomerTaxResidencyDetails.length > 0) {
                setTaxIdentifier(result?.CustomerTaxResidencyDetails[0].TaxReference);
            }
            if (result?.PersonalDetails?.DateOfBirth != null) {
                setCustomerAge(JsonDateParser.calculateAge(result?.PersonalDetails.DateOfBirth).toString());
                setCustomerDOB(JsonDateParser.getFormatDate(result?.PersonalDetails.DateOfBirth,'dd/MM/yyyy'))
            }
            if (result?.EmailAddressDetails != null && result?.EmailAddressDetails.length > 0) {
                setEmailAddress(result?.EmailAddressDetails?.[0]?.EmailAddress);
            }
            if (result?.AccountRoleByActorDetails != null && result?.AccountRoleByActorDetails.length > 0) {
                setAccountIdWithActiveRole(result?.AccountRoleByActorDetails);
            }
            setIsLoading(false);
        });
    };

    const tooltipConfig = () => {
        const config = [];
            config.push({
                title: "Account number:",
                data: ( 
                    (accountIdWithActiveRoles && accountIdWithActiveRoles.length > 0) 
                    ?   <>
                            {accountIdWithActiveRoles.map((account, index) => (
                                <div key={index}>
                                    {account.ClAccountId} <div>({getActiveRoleByRoleTypeId(account.AccountRoleType)})</div>
                                </div>
                            ))}
                        </>
                    : 'None'
                ),
            });

        if (props.clientType == clientTypeEnum.Individual){
            config.push({
                title: "Date of birth:",
                data: customerDOB ? (<span>{customerDOB} (age {customerAge})</span>) : ' ',
            });

            config.push({
                title: "Email:",
                data: emailAddress ? emailAddress : ' ',
            });

            config.push({
                title: "NINO / Identifier:",
                data: taxIdentifier ? taxIdentifier : ' ',
            });
        }

        return config;
    };

    React.useEffect(() => {
        if (!componentMounted) {
            //making sure we don't mess with focus if this is just component being mounted
            setComponentMounted(true);
            return;
        }
        if (visible) {
            if (contentRef.current) { contentRef.current.focus(); }
        } else {
            if (buttonRef.current) { buttonRef.current.focus(); }
        }
    }, [visible]);
    const onButtonClick = () => {
        if (props.trigger !== 'hover') {
            toggleVisibility();
        }
        if (props.customerId) {
            getCustomerDetail(props.customerId);
        }
    };
    const handleClickOutside = (event: any) => {
        if (!wrapperRef.current) { return; }
        if (wrapperRef && !wrapperRef.current.contains(event.target)) { closeTooltip(); }
    };
    const keyStrokesWhileOpen = (event: any) => {
        if (event.keyCode === 27) { closeTooltip(); }
    };
    const toggleVisibility = () => {
        if (visible) {
            document.removeEventListener("touchstart", handleClickOutside);
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", keyStrokesWhileOpen);
            closeTooltip();
        } else {
            if (buttonRef.current) {
                const btnRect = buttonRef.current.getBoundingClientRect();
            }
            document.addEventListener("keydown", keyStrokesWhileOpen, false);
            document.addEventListener("touchstart", handleClickOutside);
            document.addEventListener("mousedown", handleClickOutside);
            setVisibility(true);
        }
    };

    const closeTooltip = () => {
        setVisibility(false);
        setTaxIdentifier(null);
        setCustomerAge(null);
        setEmailAddress(null);
    };
    const wrapperClasses = classNames(
        "aria-tooltip-wrapper",
        { "is-expanded": visible },
        "is-dismissable",
        "is-info-icon",
        "is-text-and-dots",
        "to-the-left"
    );

    const getActiveRoleByRoleTypeId = (accountRoleType : RoleType) => {
        let accountRoleTypeName:string = "";
        switch(accountRoleType){
            case RoleType.NotSpecified :
                accountRoleTypeName = "Not Specified";
                break;
            case RoleType.IndividualAccountHolder :
                accountRoleTypeName = "Individual Account Holder";
                break;
            case RoleType.JointAccountHolder :
                accountRoleTypeName = "Joint Account Holder";
                break;            
            case RoleType.TrustAccountHolder :
                accountRoleTypeName = "Trust Account Holder";
                break;
            case RoleType.LifeAssured :
                accountRoleTypeName = "Life Assured";
                break;
            case RoleType.AdditionalLifeAssured :
                accountRoleTypeName = "Additional Life Assured";
                break;
            case RoleType.RegisteredContact :
                accountRoleTypeName = "Registered Contact";
                break;
            case RoleType.LegalRepresentative :
                accountRoleTypeName = "Legal Representative";
                break;
            case RoleType.PowerOfAttorney :
                accountRoleTypeName = "Power Of Attorney";
                break;
            case RoleType.LetterOfAuthority :
                accountRoleTypeName = "Letter Of Authority";
                break;
            case RoleType.Employer :
                accountRoleTypeName = "Employer";
                break;
            case RoleType.ExSpouse :
                accountRoleTypeName = "Ex Spouse";
                break;
            case RoleType.Dependent :
                accountRoleTypeName = "Dependent";
                break;
            case RoleType.Nominee :
                accountRoleTypeName = "Nominee";
                break;
            case RoleType.ThirdPartyPayer :
                accountRoleTypeName = "Third Party Payer";
                break;
            case RoleType.PartnerOfAccountHolder :
                accountRoleTypeName = "Partner Of Account Holder";
                break;
            case RoleType.AccountAccessLegacy :
                accountRoleTypeName = "Account Access Legacy";
                break;
            case RoleType.AccountAccess :
                accountRoleTypeName = "Account Access";
                break;
            case RoleType.Trustee :
                accountRoleTypeName = "Trustee";
                break;
            case RoleType.Settlor :
                accountRoleTypeName = "Settlor";
                break;
            case RoleType.Beneficiary :
                accountRoleTypeName = "Beneficiary";
                break;
            case RoleType.AdditionalBeneficiary :
                accountRoleTypeName = "Additional Beneficiary";
                break;
            case RoleType.TrustAppointer :
                accountRoleTypeName = "Trust Appointer";
                break;
            case RoleType.TrustProtector :
                accountRoleTypeName = "Trust Protector";
                break;
            case RoleType.ControllingPerson :
                accountRoleTypeName = "Controlling Person";
                break;
            case RoleType.DirectorController :
                accountRoleTypeName = "Director Controller";
                break;
            case RoleType.ShareholderGT25 :
                accountRoleTypeName = "Shareholder GT 25";
                break;
            case RoleType.AdditionalSignatory :
                accountRoleTypeName = "Additional Signatory";
                break;
            default :
                accountRoleTypeName = "Not Specified";
                break;
            }
        return accountRoleTypeName;
    };

    return (

        <div className={wrapperClasses} ref={wrapperRef}>
            <div className="text-and-dots-dropdown">
                <button className="trigger details-ellipsis" onClick={onButtonClick} ref={buttonRef} >
                    <span className="icon" aria-hidden={true} />
                </button>
            </div>
            {visible &&
                (<div className="tooltip-content" role="tooltip" ref={contentRef} tabIndex={-1}>
                    <div className="tooltip-content-panel w-300">
                        {
                            isLoading ? <><div className="skeleton h20 mt-10" /><div className="skeleton h20" /><div className="skeleton h20" /><div className="skeleton h20" /></> :
                        <>
                            <Typography type="narrow-dt-width" wrapCss="mt-5 mb-0 is-in-tooltip" config={tooltipConfig()} />
                            <button className="tooltip-close-button" onClick={() => closeTooltip()}>
                                <span className="icon" aria-hidden={true}></span>
                                <span className="text">Dismiss this tooltip</span>
                            </button> 
                        <hr className="mb-14 mt-0"/>
                        <div className="text-right mb-4">
                            <button className="btn primary" disabled={false}
                                onClick={() =>
                                    RoutingActions.navigateTo(
                                        Urls.get()
                                            .main.PortfolioDashboard({
                                                hierarchyId:
                                                    HierarchyIdHelper.toHierarchyIdString(
                                                        HierarchyLevelName.Customer,
                                                        props.customerId
                                                    )
                                            })
                                            .summary.getUrl()
                                    )}>
                                View client
                            </button>
                        </div>
                        </>
                        }
                    </div>
                </div>)}
        </div>
    );
};
